import {
  ArrowUpRightIcon,
  ContentSwitcher,
  IconButton,
  InfoIcon,
  MediaOverlay,
  MultiSelectDropdown,
  PanelLeftCloseIcon,
  PanelLeftOpenIcon,
  ScreenFullIcon,
  ScreenNormalIcon,
  Slider,
  TableViewIcon,
  Tooltip,
  VideoPlaybackBar,
  VideoPresentation,
  VideoPresentationTarget,
  RotateCwIcon,
  Scrollbar,
  Spinner,
  toast,
  TooltipIconButton,
  useVideoTimeline,
} from "@carbon6/c6-frontend-design";
import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from "react";
import {
  episodeTasks,
  episodeSpanSeconds,
  isMediaExpired,
  medianIntervalSeconds,
  type EpisodeInspect,
  type InspectChannel,
} from "../domain/episodeInspect";
import {
  commonWindow,
  comparability,
  streamCoverage,
  type Comparability,
} from "../feature/streamTimeFacts";
import type { InspectSession, InspectView } from "../feature/inspectSession";
import type { MappingEditorState } from "../feature/mappingEditor";
import { InspectPipeline } from "./components/InspectPipeline";
import { McpDraftPane } from "./components/McpDraftPane";
import { buildMappingTracks } from "../feature/mappingTracks";
import { openRerunWindow, rerunUnavailableReason } from "../feature/rerunWindow";
import { Viewer } from "../sim/public";
import { CameraGrid } from "./components/CameraGrid";
import { TcpCoordinates } from "./components/TcpCoordinates";
import type { TcpInspection } from "../feature/tcpPose";
import { ChannelTable } from "./components/ChannelTable";
import { MappingPanel } from "./components/mapping/MappingPanel";
import { PLAYBACK_LABELS } from "./inspectLabels";
import { useInspectPlayback } from "./useInspectPlayback";
import { useInspectSession } from "./useInspectSession";
import { useMappingEditor, type MappingEditorHandle } from "./useMappingEditor";
import styles from "./EpisodeInspector.module.css";
import { createPortal } from "react-dom";

/** 비교 가능 상태를 한 문장으로 — 무엇이 막혔는지 말하고 숫자만 보여 주지 않는다. */
function comparabilityText(state: Comparability): string {
  switch (state.kind) {
    case "comparable":
      return "같은 시각으로 비교할 수 있습니다.";
    case "clock_unknown":
      return state.streams.length === 0
        ? "공통 시간축이 확인되지 않아 채널별로만 봅니다."
        : `시계 관계가 확인되지 않은 채널이 있습니다: ${state.streams.join(", ")}`;
    case "clock_mismatch":
      return `채널이 서로 다른 시계를 씁니다(${state.clockIds.join(", ")}). 같은 시각으로 비교하지 않습니다.`;
    case "no_overlap":
      return "채널들이 함께 있는 구간이 없습니다.";
    case "empty":
      return "표시할 채널이 없습니다.";
  }
}

export type EpisodeInspectorProps = {
  /** 넓은 Dataset 창만 단계 DOM host를 제공한다. 상태와 미디어는 Inspector가 소유한다. */
  inspectHeaderHost?: HTMLElement | null;
  datasetUuid?: string;
  probeUuid: string;
  sourceId: string;
  /** 이 Probe 샘플의 episode 전부. 매핑 검증의 `샘플 전체 검증` 이 쓴다. */
  sampleSourceIds: readonly string[];
  /** 이 Probe 의 Revision. 매핑의 주소다. `null` 이면 매핑을 묻지 않는다. */
  revisionUuid: string | null;
  /** 창 최소화 같은 비활성 구간 — 재생을 멈추고 만료 갱신도 미룬다. */
  active: boolean;
  /**
   * 미디어 도구 모음의 episode 드롭다운. 로딩 · 오류에도 남아야 해서 뷰어가
   * 슬롯으로 든다(못 읽는 episode 에서 다른 건으로 옮길 길이 사라지면 안 된다).
   */
  picker?: ReactNode;
  /**
   * Dataset 조사 사실의 읽기 전용 요약. 검토 패널의 `조사 사실` 접힘 안에 서고, 펼친 동안만 마운트한다. 만드는 쪽은
   * datasets 모듈이다 — inspect 는 datasets 를 import 하지 않는다. 없으면 접힘을 두지 않는다.
   */
  factsSummary?: ReactNode;
};

/** Revision 편집본과 Dataset별 episode 세션을 연결한다. 재생 시계는 공통 c6 엔진이 소유한다. */
export function EpisodeInspector({
  inspectHeaderHost,
  active,
  datasetUuid,
  factsSummary,
  picker,
  probeUuid,
  revisionUuid,
  sampleSourceIds,
  sourceId,
}: EpisodeInspectorProps) {
  const session = useInspectSession(probeUuid, sourceId, datasetUuid);
  const mapping = useMappingEditor(revisionUuid, active, datasetUuid);
  const loader = session.loader;
  const state = useSyncExternalStore(loader.subscribe, loader.getSnapshot);
  const view = useSyncExternalStore(session.subscribe, session.getSnapshot);
  const inspect = state.status === "ready" ? state.inspect : null;
  const [headerVisible, setHeaderVisible] = useState(false);
  useLayoutEffect(() => {
    if (!inspectHeaderHost || !active) {
      setHeaderVisible(false);
      return;
    }
    const measure = () => setHeaderVisible(inspectHeaderHost.getBoundingClientRect().width > 0);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(inspectHeaderHost);
    return () => observer.disconnect();
  }, [inspectHeaderHost, active]);
  const pipeline = (
    <InspectPipeline
      state={mapping.state}
      probeUuid={probeUuid}
      selected={view.sidePanel}
      onSelect={(step) => session.setSidePanel(step)}
    />
  );
  const inHeader = headerVisible && inspectHeaderHost != null;
  const headerPipeline = inHeader ? createPortal(pipeline, inspectHeaderHost) : null;

  // 카메라 URL 만료 — 보고 있는 동안만 갱신한다(접힌 창이 왕복을 쓰지 않게). 같은 만료 시각으로
  // 한 번 갱신했으면 다시 하지 않는 판단은 세션이 든다(화면이 다시 마운트돼도 답이 같아야 한다).
  useEffect(() => {
    if (inspect === null || !active) {
      return;
    }
    const refresh = (): void => {
      if (session.shouldRefreshMedia(inspect.mediaExpiresAtMs)) {
        void loader.refreshMedia();
      }
    };
    if (isMediaExpired(inspect, Date.now())) {
      refresh();
      return;
    }
    const timer = setTimeout(refresh, Math.max(0, inspect.mediaExpiresAtMs - Date.now()));
    return () => clearTimeout(timer);
  }, [loader, session, inspect, active]);

  if (state.status === "loading") {
    return (
      <>
        {headerPipeline}
        <div className={styles.pendingInspector}>
          {inHeader ? null : pipeline}
          {picker}
          <div className={styles.center}>
            <Spinner label="점검 데이터를 불러오는 중" size="md" />
          </div>
        </div>
      </>
    );
  }

  if (state.status === "error") {
    return (
      <>
        {headerPipeline}
        <div className={styles.pendingInspector}>
          {inHeader ? null : pipeline}
          {picker}
          <div className={styles.center}>
            <p className={styles.error} role="alert">
              {state.message}
            </p>
            {state.retryable ? (
              <IconButton text="다시 시도" variant="surface" onClick={() => void loader.load()}>
                <RotateCwIcon />
              </IconButton>
            ) : null}
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {headerPipeline}
      <ReadyInspector
        pipeline={inHeader ? null : pipeline}
        datasetUuid={datasetUuid ?? null}
        revisionUuid={revisionUuid}
        active={active}
        sampleSourceIds={sampleSourceIds}
        mapping={mapping}
        probeUuid={probeUuid}
        sourceId={sourceId}
        view={view}
        inspect={state.inspect}
        channels={state.channels}
        picker={picker}
        factsSummary={factsSummary}
        refreshingMedia={state.refreshingMedia}
        session={session}
        onMediaError={() => void loader.refreshMedia()}
      />
    </>
  );
}

function ReadyInspector({
  active,
  datasetUuid,
  revisionUuid,
  channels,
  factsSummary,
  inspect,
  mapping,
  onMediaError,
  picker,
  pipeline,
  probeUuid,
  refreshingMedia,
  sampleSourceIds,
  session,
  sourceId,
  view,
}: {
  active: boolean;
  datasetUuid: string | null;
  revisionUuid: string | null;
  sampleSourceIds: readonly string[];
  mapping: MappingEditorHandle;
  probeUuid: string;
  sourceId: string;
  channels: readonly InspectChannel[];
  inspect: EpisodeInspect;
  picker: ReactNode;
  factsSummary: ReactNode;
  pipeline: ReactNode;
  refreshingMedia: boolean;
  session: InspectSession;
  view: InspectView;
  onMediaError: () => void;
}) {
  const inspectorRef = useRef<HTMLDivElement>(null);
  const playbackScopeRef = useRef<HTMLElement>(null);
  const [overlay, setOverlay] = useState({ open: false, mounted: false });
  const [wide, setWide] = useState(true);
  useEffect(() => {
    if (!active) setOverlay((current) => (current.open ? { ...current, open: false } : current));
  }, [active]);
  // 화면 해상도가 아닌 실제 작업대 폭으로 배치와 숨은 3D 렌더링을 함께 결정한다.
  useLayoutEffect(() => {
    const element = inspectorRef.current;
    if (element === null) return;
    const measure = () => {
      const width = element.getBoundingClientRect().width;
      // 최소화로 측정 폭이 사라져도 마지막 배치를 보존한다.
      if (width > 0) setWide(width >= 700);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  const shownVideos = useMemo(() => {
    const selected = view.cameraKeys.flatMap((key) =>
      inspect.videos.filter((video) => video.key === key),
    );
    return selected.length > 0 ? selected : inspect.videos.slice(0, 1);
  }, [inspect.videos, view.cameraKeys]);
  const hasVideo = shownVideos.length > 0;
  const mediaActive = active && (overlay.mounted || wide || view.compactTab === "media");
  const timeline = useInspectPlayback({
    videos: shownVideos,
    channels,
    durationSeconds: episodeSpanSeconds(channels),
    active: mediaActive,
    memory: session,
  });
  const currentSeconds = useVideoTimeline(timeline, (snapshot) => snapshot.currentTimeSeconds);
  const byKey = useMemo(
    () => new Map(channels.map((channel) => [channel.key, channel])),
    [channels],
  );
  const { draft, focus } = mapping.state;
  // 편집본이 바뀔 때마다 다시 계산한다. 저장을 기다리지 않는다.
  const sim = useMemo(() => buildMappingTracks(draft, focus, byKey), [draft, focus, byKey]);
  const [tcpInspection, setTcpInspection] = useState<{
    input: typeof sim;
    value: TcpInspection;
  } | null>(null);
  const inspection = tcpInspection?.input === sim ? tcpInspection.value : null;
  useEffect(() => {
    if (inspection === null) return;
    timeline.pause();
    if (currentSeconds !== inspection.seconds) timeline.seekToSeconds(inspection.seconds);
  }, [inspection, timeline, currentSeconds]);
  const tasks = episodeTasks(inspect);
  // 채널마다 rate 가 다르다. 그래서 "몇 행" 은 아무것도 말하지 않는다. 함께 볼 수 있는 구간을 센다.
  const timeFacts = useMemo(() => {
    const coverages = channels.map((channel) => streamCoverage(channel));
    return {
      window: commonWindow(coverages),
      state: comparability(channels, inspect.timeOrigin),
    };
  }, [channels, inspect]);
  // 한 칸 이동은 시간 원점 채널의 간격을 따른다. 원점이 없으면 0.1 초다.
  const stepSeconds = medianIntervalSeconds(byKey.get(inspect.timeOrigin.channel ?? "")) ?? 0.1;

  const selectedKeys = shownVideos.map((video) => video.key);
  const media = (
    <section className={styles.mediaRegion} aria-label="영상과 핸드 리뷰" ref={playbackScopeRef}>
      <div className={styles.mediaControls}>
        <div className={styles.picker}>{picker}</div>
        {hasVideo ? (
          <Tooltip content="최대 2개를 선택할 수 있습니다." describeTrigger>
            <MultiSelectDropdown
              label="선택 카메라"
              size="sm"
              className={styles.cameras}
              selectedValues={selectedKeys}
              renderSelectedValue={(option) => option.label}
              closeOnSelect={false}
              clearSelectionLabel="선택 해제"
              options={inspect.videos.map((video) => ({
                value: video.key,
                label: video.key,
                disabled: selectedKeys.length === 2 && !selectedKeys.includes(video.key),
              }))}
              onSelectionChange={(keys) => {
                const valid = keys.filter((key) =>
                  inspect.videos.some((video) => video.key === key),
                );
                if (valid.length === 0 || valid.length > 2) return;
                // 첫 영상 fallback도 기존 선택으로 취급해 두 번째 선택 때 좌우가 뒤집히지 않게 한다.
                session.setCameras([
                  ...selectedKeys.filter((key) => valid.includes(key)),
                  ...valid.filter((key) => !selectedKeys.includes(key)),
                ]);
              }}
            />
          </Tooltip>
        ) : null}
        <ContentSwitcher
          label="미디어 배치"
          size="sm"
          value={view.mediaLayout}
          options={[
            { value: "pip", label: "PIP" },
            { value: "split", label: "분할" },
            {
              value: "overlay",
              label: "겹침",
              tooltip: "영상 위에 핸드를 겹쳐 표시합니다. 방향을 비교하는 참고 화면입니다.",
            },
          ]}
          onValueChange={(value) => {
            if (value === "pip" || value === "split" || value === "overlay")
              session.setMediaLayout(value);
          }}
        />
        <TooltipIconButton
          aria-label="화면 교환"
          tooltip="영상과 핸드의 자리를 바꿉니다."
          disabledTooltip={
            view.mediaLayout === "overlay"
              ? "겹침에서는 영상 위에 핸드를 표시합니다. PIP나 분할에서 자리를 바꿀 수 있습니다."
              : "이 episode에는 카메라 영상이 없습니다."
          }
          variant="surface"
          size="sm"
          disabled={!hasVideo || view.mediaLayout === "overlay"}
          onClick={() => session.setMediaPanel(view.mediaPanel === "sim" ? "videos" : "sim")}
        >
          <RotateCwIcon />
        </TooltipIconButton>
        <label className={styles.opacity}>
          <span>Opacity</span>
          <Slider
            aria-label="메시 Opacity"
            min={0}
            max={100}
            step={1}
            value={Math.round(view.meshOpacity * 100)}
            getAriaValueText={(value) => `${value}%`}
            onValueChange={(value) => session.setMeshOpacity(value / 100)}
          />
          <span>{Math.round(view.meshOpacity * 100)}%</span>
        </label>
        <TooltipIconButton
          aria-label={overlay.open ? "확대 닫기" : "미디어 확대"}
          tooltip={overlay.open ? "확대 닫기" : "미디어 확대"}
          variant="surface"
          size="sm"
          onClick={() =>
            setOverlay((current) =>
              current.open ? { ...current, open: false } : { open: true, mounted: true },
            )
          }
        >
          {overlay.open ? <ScreenNormalIcon /> : <ScreenFullIcon />}
        </TooltipIconButton>
        {wide && !overlay.mounted ? (
          <TooltipIconButton
            aria-label={view.reviewPanelOpen ? "검토 패널 접기" : "검토 패널 펼치기"}
            tooltip={view.reviewPanelOpen ? "검토 패널 접기" : "검토 패널 펼치기"}
            variant="surface"
            size="sm"
            onClick={() => session.setReviewPanelOpen(!view.reviewPanelOpen)}
          >
            {view.reviewPanelOpen ? <PanelLeftCloseIcon /> : <PanelLeftOpenIcon />}
          </TooltipIconButton>
        ) : null}
        <TooltipIconButton
          aria-label="리뷰 정보"
          variant="surface"
          size="sm"
          tooltip={
            <div className={styles.information}>
              <span>
                공통 구간 {timeFacts.window.seconds.toFixed(2)}s / 전체{" "}
                {timeFacts.window.spanSeconds.toFixed(2)}s (
                {Math.round(timeFacts.window.fraction * 100)}%)
              </span>
              <span>{comparabilityText(timeFacts.state)}</span>
              {tasks.length > 0 ? <span>task: {tasks.join(" / ")}</span> : null}
              <span>선택한 손을 진하게 표시합니다. 그리퍼 출력 0은 열림, 1은 닫힘입니다.</span>
              <span>영상 위에 핸드를 겹쳐 표시합니다. 방향을 비교하는 참고 화면입니다.</span>
            </div>
          }
        >
          <InfoIcon />
        </TooltipIconButton>
        <TooltipIconButton
          aria-label="원본 채널"
          tooltip="원본 채널"
          variant="surface"
          size="sm"
          onClick={() => {
            session.setSidePanel("channels");
            setOverlay((current) => ({ ...current, open: false }));
          }}
        >
          <TableViewIcon />
        </TooltipIconButton>
        <RerunWindowButton probeUuid={probeUuid} sourceId={sourceId} mapping={mapping.state} />
        {refreshingMedia ? <Spinner size="sm" label="영상 주소 갱신 중" /> : null}
      </div>
      <div
        className={styles.mediaStage}
        data-main={view.mediaPanel}
        data-arrangement={view.mediaLayout}
        data-has-video={hasVideo}
      >
        <section className={styles.simStage} aria-label="Sim">
          {sim.tracks.length === 0 ? (
            <p className={styles.pending} role="status">
              그릴 손이 없습니다. 매핑에서 위치·자세를 고르면 핸드를 표시합니다.
            </p>
          ) : (
            <Viewer
              className={styles.sim}
              tracks={sim.tracks}
              transport={timeline}
              active={mediaActive}
              mode="convention"
              meshOpacity={view.meshOpacity}
              inspection={inspection}
              showSampleStatus={false}
              presentation={view.mediaLayout === "overlay" ? "overlay" : "surface"}
            />
          )}
        </section>
        {hasVideo ? (
          <section className={styles.videoStage} aria-label="영상 참고">
            <CameraGrid
              videos={shownVideos}
              channels={byKey}
              currentSeconds={currentSeconds}
              transport={timeline}
              onMediaError={onMediaError}
              active={mediaActive}
            />
          </section>
        ) : null}
      </div>
      <TcpCoordinates
        tracks={sim.tracks}
        seconds={currentSeconds}
        inspection={inspection}
        active={mediaActive}
        onInspection={(value) => {
          if (value !== null) timeline.pause();
          setTcpInspection(value === null ? null : { input: sim, value });
        }}
      />
      <fieldset className={styles.playbackControls} disabled={inspection !== null}>
        <VideoPlaybackBar
          transport={timeline}
          labels={PLAYBACK_LABELS}
          stepSeconds={stepSeconds}
          layout={wide || overlay.mounted ? "inline" : "stacked"}
          transportControls="full"
          size="sm"
          showPlaybackRate
          keyboardScopeRef={playbackScopeRef}
        />
      </fieldset>
    </section>
  );

  return (
    <div
      className={styles.inspector}
      ref={inspectorRef}
      data-layout={wide ? "wide" : "compact"}
      data-compact-tab={view.compactTab}
      data-panel-open={view.reviewPanelOpen}
      data-header={pipeline === null}
    >
      <VideoPresentation content={media}>
        {pipeline === null ? null : <div className={styles.pipeline}>{pipeline}</div>}
        {!wide ? (
          <div className={styles.compactTabs}>
            {view.compactTab === "panel" ? picker : null}
            <ContentSwitcher
              label="작업 영역"
              value={view.compactTab}
              options={[
                { value: "media", label: "리뷰", ariaLabel: "영상과 핸드 리뷰" },
                { value: "panel", label: "설정" },
              ]}
              onValueChange={(value) => {
                if (value === "media" || value === "panel") session.setCompactTab(value);
              }}
            />
          </div>
        ) : null}
        <VideoPresentationTarget
          className={styles.mediaHost}
          active={!overlay.mounted}
          hidden={!wide && view.compactTab !== "media"}
        />
        <section
          className={styles.sideRegion}
          aria-label="매핑과 원본"
          hidden={wide ? !view.reviewPanelOpen : view.compactTab !== "panel"}
        >
          {view.sidePanel === "mcp" ? (
            <McpDraftPane
              active={active}
              context={{ datasetUuid, revisionUuid, probeUuid, sourceId, sampleSourceIds }}
              editor={mapping.editor}
              state={mapping.state}
              onReview={() => session.setSidePanel("mapping")}
            />
          ) : view.sidePanel === "channels" ? (
            <Scrollbar className={styles.regionScroll} gutter="auto" aria-label="원본 채널 내용">
              <ChannelTable inspect={inspect} channels={channels} currentSeconds={currentSeconds} />
            </Scrollbar>
          ) : (
            <>
              {sim.notes.length > 0 && view.sidePanel === "mapping" ? (
                <details className={styles.mediaNotes}>
                  <summary>핸드 표시 안내</summary>
                  <Scrollbar className={styles.notesScroll} gutter="auto">
                    <ul aria-label="3D 대조 안내">
                      {sim.notes.map((note) => (
                        <li key={note}>{note}</li>
                      ))}
                    </ul>
                  </Scrollbar>
                </details>
              ) : null}
              {factsSummary == null ? null : (
                <FactsSummaryDetails>{factsSummary}</FactsSummaryDetails>
              )}
              <MappingPanel
                view={view.sidePanel}
                onEditMapping={() => session.setSidePanel("mapping")}
                onMcpDraft={() => session.setSidePanel("mcp")}
                editor={mapping.editor}
                state={mapping.state}
                inspect={inspect}
                probeUuid={probeUuid}
                sourceId={sourceId}
                sampleSourceIds={sampleSourceIds}
                scrollClassName={styles.regionScroll}
              />
            </>
          )}
        </section>
        <MediaOverlay
          open={overlay.open}
          aria-label="영상과 핸드 확대"
          onClose={() => setOverlay((current) => ({ ...current, open: false }))}
          onClosed={() => setOverlay({ open: false, mounted: false })}
        >
          <VideoPresentationTarget
            className={styles.overlayHost}
            style={{ width: "calc(100vw - 3rem)" }}
            active={overlay.mounted}
          />
        </MediaOverlay>
      </VideoPresentation>
    </div>
  );
}

/**
 * Rerun 새 창 뷰어를 여는 버튼. 뷰어는 저장된 매핑을 쓰므로 저장본이 확인될 때만 켠다.
 * 끈 버튼은 툴팁으로 사유를 말한다. 편집본이 저장본과 다르면 새 창이 무엇을 보이는지 툴팁으로 말한다.
 */
function RerunWindowButton({
  mapping,
  probeUuid,
  sourceId,
}: {
  mapping: MappingEditorState;
  probeUuid: string;
  sourceId: string;
}) {
  const reason = rerunUnavailableReason(mapping.saved);
  const open = (): void => {
    if (!openRerunWindow(probeUuid, sourceId)) {
      toast.warning(
        "브라우저가 새 창을 막았습니다. 이 사이트의 팝업을 허용한 뒤 다시 열어 주세요.",
      );
    }
  };
  return (
    <TooltipIconButton
      aria-label="Rerun 새 창"
      variant="surface"
      size="sm"
      disabled={reason !== null}
      disabledTooltip={reason}
      tooltip={
        mapping.dirty
          ? "새 창은 저장된 매핑을 씁니다. 저장하지 않은 변경은 새 창에 보이지 않습니다."
          : "Rerun 새 창"
      }
      onClick={open}
    >
      <ArrowUpRightIcon />
    </TooltipIconButton>
  );
}

/**
 * 조사 사실 요약의 접힘. 펼친 동안만 내용을 마운트한다 — 요약은 마운트되면 사실을 받고 변경을 따른다. 접힌 채로
 * 두면 보이지 않는 요약을 점검을 열 때마다 · episode 를 바꿀 때마다 받는다.
 */
function FactsSummaryDetails({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <details className={styles.mediaNotes} onToggle={(event) => setOpen(event.currentTarget.open)}>
      <summary>조사 사실</summary>
      {open ? (
        <Scrollbar className={styles.notesScroll} gutter="auto">
          {children}
        </Scrollbar>
      ) : null}
    </details>
  );
}
